import { AlmanacList } from "portal/components/almanac/AlmanacList";
import { buildPermission } from "portal/utils/auth";
import { Config } from "portal/components/config/Config";
import { convert } from "portal/utils/units/units";
import { DATE_PATH_FORMAT } from "portal/utils/dates";
import { DateTime } from "luxon";
import {
  FeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import { getActiveAlarms } from "portal/utils/robots";
import { getRobotPath, Path, RobotSubpath, Slug } from "portal/utils/routing";
import { GlobalHotKeys, KeyMap } from "react-hotkeys";
import { GROUP_ROBOT } from "portal/utils/hotkeys";
import { Header } from "portal/components/header/Header";
import { LaserWeederSummary } from "./LaserWeederSummary";
import {
  Navigate,
  RouteObject,
  useLocation,
  useNavigate,
  useParams,
  useRoutes,
} from "react-router-dom";
import { NewRobotField } from "portal/views/robots/NewRobotField";
import { Page } from "portal/components/Page";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalEndpoints } from "portal/state/portalApi";
import { range } from "portal/utils/arrays";
import { ResponsiveSubnav } from "portal/components/ResponsiveSubnav";
import { RobotActivePlantProfile } from "../robots/RobotActivePlantProfile";
import { RobotAdmin } from "../robots/RobotAdmin";
import { RobotAlarmList } from "portal/views/robots/RobotAlarmList";
import { RobotCropsPage } from "portal/views/robots/crops/RobotCropsPage";
import { RobotDiscriminatorList } from "../robots/RobotDiscriminatorList";
import { RobotFieldList } from "../robots/RobotFieldList";
import { RobotHardware } from "../robots/RobotHardware";
import { RobotHistory } from "portal/views/robots/RobotHistory";
import { RobotModelinator } from "../robots/RobotModelinator";
import { RobotSelector } from "portal/components/robots/RobotSelector";
import { RobotSupport } from "portal/views/robots/RobotSupport";
import { RobotUploads } from "portal/views/robots/RobotUploads";
import { RobotView } from "../robots/AnyRobotPage";
import { Skeleton } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { titleCase, valueOrLoading } from "portal/utils/strings";
import { UnauthorizedPage } from "portal/components/UnauthorizedPage";
import {
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { useCanSeePlantProfilesPage } from "portal/views/plantProfiles/permissions";
import { useJigsawMetrics } from "../../utils/hooks/useJigsawMetrics";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { VelocityEstimatorList } from "portal/components/velocityEstimator/VelocityEstimatorList";
import React, { FunctionComponent, useState } from "react";

interface LaserWeederRobotPageProps {
  offerViewSwitchButton?: RobotView;
}

/** Detail UUIDs on these tabs belong to the current robot; drop them on switch. */
const ROBOT_TABS_RESET_ON_SWITCH: readonly RobotSubpath[] = [
  RobotSubpath.ALMANAC,
  RobotSubpath.DISCRIMINATOR,
  RobotSubpath.VELOCITY_ESTIMATOR,
];

const _LaserWeederRobotPage: FunctionComponent<LaserWeederRobotPageProps> = ({
  offerViewSwitchButton,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { i18n, t } = useTranslation();
  const useJigsaw = useJigsawMetrics();

  // ### ROBOT
  const { serial } = useParams();
  const { data: summary, isSuccess } = useQueryPopups(
    portalEndpoints.getRobot.useQuery(
      serial ? { language: i18n.language, serial, useJigsaw } : skipToken,
      {
        pollingInterval: convert(1).from("min").to("ms"),
      }
    )
  );
  const robotCustomerUuid = summary?.customer?.uuid;
  const { isEnabled: hasAlmanac } = useFeatureFlagForCustomer(
    FeatureFlag.ALMANAC,
    robotCustomerUuid
  );
  const { isEnabled: hasTargetVelocityEstimator } = useFeatureFlagForCustomer(
    FeatureFlag.TARGET_VELOCITY_ESTIMATOR,
    robotCustomerUuid
  );

  // ### ROUTING
  const keyMap: KeyMap = {
    GO_SUMMARY: {
      name: t("utils.actions.goToLong", {
        subject: t("views.fleet.robots.summary.navTitle"),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r s",
    },
  };
  const routes: RouteObject[] = [
    {
      path: Path.BASE,
      element: (
        <LaserWeederSummary offerViewSwitchButton={offerViewSwitchButton} />
      ),
    },
  ];

  // jobs
  const isJobsAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.jobs,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.jobs,
        PermissionDomain.all
      ),
    ],
  });
  if (isJobsAuthorized) {
    routes.push(
      {
        path: `${RobotSubpath.HISTORY}/:startDate/:endDate`,
        element: <RobotHistory />,
      },
      {
        path: `${RobotSubpath.HISTORY}/*`,
        element: (() => {
          const today = DateTime.local();
          const lastWeek = today.minus({ days: 7 });
          return (
            <Navigate
              to={`${getRobotPath(
                serial,
                RobotSubpath.HISTORY
              )}/${lastWeek.toFormat(DATE_PATH_FORMAT)}/${today.toFormat(
                DATE_PATH_FORMAT
              )}`}
            />
          );
        })(),
      }
    );
    keyMap.GO_HISTORY = {
      name: t("utils.actions.goToLong", {
        subject: t("views.fleet.robots.history.navTitle"),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r h",
    };
  }

  // crops
  const isCropsAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.crops_basic,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.crops_basic,
        PermissionDomain.all
      ),
    ],
  });
  if (isCropsAuthorized) {
    routes.push(
      {
        path: `${RobotSubpath.CROPS}/:cropId?`,
        element: <RobotCropsPage />,
      },
      {
        path: `${RobotSubpath.CROPS}/:cropId/:modelId`,
        element: <RobotModelinator />,
      }
    );
    keyMap.GO_CROPS = {
      name: t("utils.actions.goToLong", {
        subject: titleCase(t("models.crops.crop_other")),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r o",
    };
  }

  // alarms
  const isAlarmsAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.alarms_customer,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.alarms_customer,
        PermissionDomain.all
      ),
    ],
  });
  if (isAlarmsAuthorized) {
    routes.push({ path: RobotSubpath.ALARMS, element: <RobotAlarmList /> });
    keyMap.GO_ALARMS = {
      name: t("utils.actions.goToLong", {
        subject: titleCase(t("models.alarms.alarm_other")),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r a",
    };
  }

  // chat
  const isChatAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.chat,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.chat,
        PermissionDomain.all
      ),
    ],
  });
  if (isChatAuthorized) {
    routes.push({ path: RobotSubpath.SUPPORT, element: <RobotSupport /> });
    keyMap.GO_SUPPORT = {
      name: t("utils.actions.goToLong", {
        subject: t("views.fleet.robots.support.navTitle"),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r t",
    };
  }

  // almanac
  const isAlmanacAuthorized =
    useAuthorizationRequired({
      or: [
        buildPermission(
          PermissionAction.read,
          PermissionResource.almanacs,
          PermissionDomain.customer
        ),
        buildPermission(
          PermissionAction.read,
          PermissionResource.almanacs,
          PermissionDomain.all
        ),
      ],
    }) && hasAlmanac;
  if (isAlmanacAuthorized) {
    routes.push({
      path: `${RobotSubpath.ALMANAC}/*`,
      element: serial ? (
        <AlmanacList
          serial={serial}
          basePath={getRobotPath(serial, RobotSubpath.ALMANAC)}
          useListQuery={portalEndpoints.listAlmanacsForRobot.useQuery}
          useGetQuery={portalEndpoints.getAlmanac.useQuery}
          useDeleteMutation={portalEndpoints.deleteAlmanac.useMutation}
          useSetMutation={portalEndpoints.setAlmanac.useMutation}
        />
      ) : (
        <></>
      ),
    });
    keyMap.GO_ALMANAC = {
      name: t("utils.actions.goToLong", {
        subject: titleCase(t("models.almanacs.almanac_other")),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r l",
    };
  }

  // discriminator
  const isDiscriminatorAuthorized =
    useAuthorizationRequired({
      or: [
        buildPermission(
          PermissionAction.read,
          PermissionResource.discriminators,
          PermissionDomain.customer
        ),
        buildPermission(
          PermissionAction.read,
          PermissionResource.discriminators,
          PermissionDomain.all
        ),
      ],
    }) && hasAlmanac;
  if (isDiscriminatorAuthorized) {
    routes.push({
      path: `${RobotSubpath.DISCRIMINATOR}/*`,
      element: <RobotDiscriminatorList />,
    });
    keyMap.GO_DISCRIMINATOR = {
      name: t("utils.actions.goToLong", {
        subject: titleCase(t("models.discriminators.discriminator_other")),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r d",
    };
  }

  // category collection — active profile only (list lives at /plant-profiles)
  const isCategoryCollectionAuthorized = useCanSeePlantProfilesPage();
  if (isCategoryCollectionAuthorized) {
    routes.push(
      {
        path: `${RobotSubpath.PLANT_PROFILE}/${Slug.EDIT}`,
        element: <RobotActivePlantProfile key={serial} />,
      },
      {
        path: RobotSubpath.PLANT_PROFILE,
        element: <RobotActivePlantProfile key={serial} />,
      }
    );
    keyMap.GO_CATEGORY_COLLECTION = {
      name: t("utils.actions.goToLong", {
        subject: t(
          "models.categoryCollectionProfiles.categoryCollectionProfile_title_one"
        ),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r p",
    };
  }

  // velocity estimator
  const isVelocityEstimatorAuthorized =
    useAuthorizationRequired({
      or: [
        buildPermission(
          PermissionAction.read,
          PermissionResource.velocity_estimators,
          PermissionDomain.customer
        ),
        buildPermission(
          PermissionAction.read,
          PermissionResource.velocity_estimators,
          PermissionDomain.all
        ),
      ],
    }) && hasTargetVelocityEstimator;
  if (isVelocityEstimatorAuthorized) {
    routes.push({
      path: `${RobotSubpath.VELOCITY_ESTIMATOR}/*`,
      element: serial ? (
        <VelocityEstimatorList
          serial={serial}
          basePath={getRobotPath(serial, RobotSubpath.VELOCITY_ESTIMATOR)}
          useListQuery={portalEndpoints.listTargetVelocityEstimators.useQuery}
          useGetQuery={portalEndpoints.getTargetVelocityEstimator.useQuery}
          useSetMutation={
            portalEndpoints.setTargetVelocityEstimator.useMutation
          }
          useDeleteMutation={
            portalEndpoints.deleteTargetVelocityEstimator.useMutation
          }
        />
      ) : (
        <></>
      ),
    });
    keyMap.GO_VELOCITYESTIMATOR = {
      name: t("utils.actions.goToLong", {
        subject: titleCase(
          t("models.velocityEstimators.velocityEstimator_other")
        ),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r v",
    };
  }

  // config
  const isConfigAuthorized = useAuthorizationRequired(
    // NOTE: In principle, this should also allow `read:configs:customer`.
    // But currently (1) we don't actually want customers to see configs, yet
    // (2) we're granting all customers read-write permissions to their own
    // configs to unbreak things like crops that use configs as a backing store
    // and don't have a smart deputy yet. So we leave the API open but just
    // hide the UI.
    buildPermission(
      PermissionAction.read,
      PermissionResource.configs,
      PermissionDomain.all
    )
  );
  if (isConfigAuthorized) {
    routes.push({
      path: `${RobotSubpath.CONFIG}/*`,
      element: (
        <Config
          serial={serial}
          customerSerial={summary?.robot?.customerSerial}
          basePath={getRobotPath(serial, RobotSubpath.CONFIG)}
        />
      ),
    });
    keyMap.GO_CONFIG = {
      name: t("utils.actions.goToLong", {
        subject: titleCase(t("models.configs.config_one")),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r c",
    };
  }

  // hardware
  const isHardwareAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.hardware,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.hardware,
        PermissionDomain.all
      ),
    ],
  });
  if (isHardwareAuthorized) {
    routes.push({
      path: `${RobotSubpath.HARDWARE}/*`,
      element: <RobotHardware />,
    });
    keyMap.GO_HARDWARE = {
      name: t("utils.actions.goToLong", {
        subject: t("views.fleet.robots.hardware.title"),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r r",
    };
  }

  // uploaded images - only relevant for carbon internal users
  const isUploadsReadAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.uploads,
      PermissionDomain.all
    )
  );
  if (isUploadsReadAuthorized) {
    routes.push({ path: RobotSubpath.UPLOADS, element: <RobotUploads /> });
    keyMap.GO_UPLOADS = {
      name: t("utils.actions.goToLong", {
        subject: titleCase(t("models.images.image_other")),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r u",
    };
  }

  // admin
  const isAdminAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.admin_cloud,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.admin_cloud,
        PermissionDomain.all
      ),
    ],
  });
  if (isAdminAuthorized) {
    routes.push({ path: RobotSubpath.ADMIN, element: <RobotAdmin /> });
    keyMap.GO_ADMIN = {
      name: t("utils.actions.goToLong", {
        subject: t("views.admin.title"),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r a",
    };
  }

  // farms
  const isFarmsAuthorized = useAuthorizationRequired({
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.farms,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.farms,
        PermissionDomain.all
      ),
    ],
  });
  if (isFarmsAuthorized) {
    routes.push(
      {
        path: `${RobotSubpath.FIELDS}`,
        element: <RobotFieldList />,
      },
      {
        path: `${RobotSubpath.FIELDS}/${Slug.NEW}`,
        element: <NewRobotField serial={serial} />,
      }
    );
    keyMap.GO_FIELDS = {
      name: t("utils.actions.goToLong", {
        subject: titleCase(t("models.fieldDefinitions.fieldDefinition_other")),
      }),
      group: GROUP_ROBOT,
      action: "keyup",
      sequence: "r f",
    };
  }

  const element = useRoutes(routes);
  const [subtitle, setSubtitle] = useState<string>("");

  let name: string = "";
  if (isSuccess) {
    name = valueOrLoading(summary.robot?.customerSerial, t);
  }

  return (
    <>
      <GlobalHotKeys
        allowChanges
        keyMap={keyMap}
        handlers={{
          GO_SUMMARY: () => navigate(getRobotPath(serial)),
          GO_HISTORY: () =>
            navigate(getRobotPath(serial, RobotSubpath.HISTORY)),
          GO_CONFIG: () => navigate(getRobotPath(serial, RobotSubpath.CONFIG)),
          GO_CROPS: () => navigate(getRobotPath(serial, RobotSubpath.CROPS)),
          GO_ALARMS: () => navigate(getRobotPath(serial, RobotSubpath.ALARMS)),
          GO_SUPPORT: () =>
            navigate(getRobotPath(serial, RobotSubpath.SUPPORT)),
          GO_HARDWARE: () =>
            navigate(getRobotPath(serial, RobotSubpath.HARDWARE)),
          GO_ALMANAC: () =>
            navigate(getRobotPath(serial, RobotSubpath.ALMANAC)),
          GO_DISCRIMINATOR: () =>
            navigate(getRobotPath(serial, RobotSubpath.DISCRIMINATOR)),
          GO_CATEGORY_COLLECTION: () =>
            navigate(getRobotPath(serial, RobotSubpath.PLANT_PROFILE)),
          GO_VELOCITYESTIMATOR: () =>
            navigate(getRobotPath(serial, RobotSubpath.VELOCITY_ESTIMATOR)),
          GO_UPLOADS: () =>
            navigate(getRobotPath(serial, RobotSubpath.UPLOADS)),
          GO_ADMIN: () => navigate(getRobotPath(serial, RobotSubpath.ADMIN)),
          GO_FIELDS: () => navigate(getRobotPath(serial, RobotSubpath.FIELDS)),
        }}
      />
      <Header
        hideTitle={true}
        title={name}
        subtitle={subtitle}
        parentLink={Path.FLEET}
      >
        <RobotSelector
          serial={serial}
          customerSerial={summary?.robot?.customerSerial}
          onChange={({ robot }) => {
            if (!serial || !robot) {
              return;
            }
            const rest = pathname.slice(getRobotPath(serial).length);
            const tab = ROBOT_TABS_RESET_ON_SWITCH.find(
              (subpath) => rest === subpath || rest.startsWith(`${subpath}/`)
            );
            navigate(
              tab
                ? getRobotPath(robot.serial, tab)
                : pathname.replace(serial, robot.serial)
            );
          }}
          typography="h1"
          typographyClassName="text-lg"
        />
        {summary ? (
          <ResponsiveSubnav
            onChange={(index, name) => setSubtitle(name)}
            pages={[
              {
                label: t("views.fleet.robots.summary.navTitle"),
                to: getRobotPath(serial),
              },
              {
                label: t("views.fleet.robots.history.navTitle"),
                to: `${getRobotPath(serial, RobotSubpath.HISTORY)}/*`,
                hidden: !isJobsAuthorized,
              },
              {
                label: titleCase(t("models.configs.config_one")),
                to: `${getRobotPath(serial, RobotSubpath.CONFIG)}/*`,
                hidden: !isConfigAuthorized,
              },
              {
                count: getActiveAlarms(summary).length,
                label: titleCase(t("models.alarms.alarm_other")),
                to: getRobotPath(serial, RobotSubpath.ALARMS),
                hidden: !isAlarmsAuthorized,
              },
              {
                label: t("views.fleet.robots.support.navTitle"),
                to: getRobotPath(serial, RobotSubpath.SUPPORT),
                hidden: !isChatAuthorized,
              },
              {
                label: titleCase(t("models.crops.crop_other")),
                to: getRobotPath(serial, RobotSubpath.CROPS),
                hidden: !isCropsAuthorized,
              },
              {
                label: titleCase(t("models.almanacs.almanac_other")),
                to: `${getRobotPath(serial, RobotSubpath.ALMANAC)}/*`,
                hidden: !isAlmanacAuthorized,
              },
              {
                label: titleCase(
                  t("models.discriminators.discriminator_other")
                ),
                to: `${getRobotPath(serial, RobotSubpath.DISCRIMINATOR)}/*`,
                hidden: !isDiscriminatorAuthorized,
              },
              {
                label: titleCase(
                  t("models.velocityEstimators.velocityEstimator_other")
                ),
                to: `${getRobotPath(
                  serial,
                  RobotSubpath.VELOCITY_ESTIMATOR
                )}/*`,
                hidden: !isVelocityEstimatorAuthorized,
              },
              {
                label: t(
                  "models.categoryCollectionProfiles.categoryCollectionProfile_title_one"
                ),
                to: `${getRobotPath(serial, RobotSubpath.PLANT_PROFILE)}/*`,
                hidden: !isCategoryCollectionAuthorized,
              },
              {
                label: titleCase(t("views.fleet.robots.hardware.title")),
                to: getRobotPath(serial, RobotSubpath.HARDWARE),
                hidden: !isHardwareAuthorized,
              },
              {
                label: titleCase(t("models.images.image_other")),
                to: getRobotPath(serial, RobotSubpath.UPLOADS),
                hidden: !isUploadsReadAuthorized,
              },
              {
                label: titleCase(
                  t("models.fieldDefinitions.fieldDefinition_other")
                ),
                to: getRobotPath(serial, RobotSubpath.FIELDS),
                hidden: !isFarmsAuthorized,
              },
              {
                label: t("views.admin.title"),
                to: getRobotPath(serial, RobotSubpath.ADMIN),
                hidden: !isAdminAuthorized,
              },
            ]}
          />
        ) : (
          <>
            {range(3).map((index) => (
              <Skeleton
                variant="rectangular"
                className="w-16 h-full"
                key={index}
              />
            ))}
          </>
        )}
      </Header>
      <Page>{element}</Page>
    </>
  );
};

export const LaserWeederRobotPage = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.robots,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.robots,
        PermissionDomain.all
      ),
    ],
  },
  _LaserWeederRobotPage,
  <UnauthorizedPage />
);
